import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Chart from "chart.js/auto";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// GradientLineChart configurations
import configs from "examples/Charts/LineCharts/GradientLineChart/configs";

// Soft UI Dashboard React base styles
import colors from "assets/theme/base/colors";

function GradientLineChart({ title, description, height, chart }) {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const labels = chart.labels || [];
    const rawDatasets = chart.datasets || [];

    // Destroy existing chart instance
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");

    // Prepare datasets with gradient backgrounds
    const processedDatasets = rawDatasets.map((dataset) => {
      const colorMain = colors[dataset.color]?.main || colors.dark.main;
      return {
        ...dataset,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 3,
        borderColor: colorMain,
        fill: true,
        backgroundColor: gradientChartLine(canvasRef.current, colorMain),
      };
    });

    // Get config data and options
    const { data, options } = configs(labels, processedDatasets);

    // Create new chart instance
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data,
      options,
    });

    // Cleanup on unmount
    return () => {
      chartInstance.current && chartInstance.current.destroy();
    };
  }, [chart]);

  const chartElement = (
    <SoftBox sx={{ height }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
    </SoftBox>
  );

  return (
    <Card>
      {title && (
        <SoftBox px={2} pt={2}>
          <SoftTypography variant="h6">{title}</SoftTypography>
        </SoftBox>
      )}
      {description && (
        <SoftBox px={2} pb={1}>
          <SoftTypography variant="button" color="text">
            {description}
          </SoftTypography>
        </SoftBox>
      )}
      {chartElement}
    </Card>
  );
}

GradientLineChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
};

GradientLineChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default GradientLineChart;
